import React from "react";
import { useTranslation } from "react-i18next";
import styled from "@emotion/styled";
import { Box, Typography, useMediaQuery, Theme } from "@mui/material";
import { LoadingPlaceHolder, ScrollableWithGradients } from "@components";
import { useArivaTimeline } from "./useArivaTimeline";
import { TimelineArticle } from "./types";
import { TimelineCard } from "./TimelineCard";

const Wrapper = styled(Box)({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
});

const SIDEBAR_MAX_HEIGHT = 415;

const GroupTitle = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
  "&:first-of-type": {
    marginTop: 0,
  },
  textTransform: "uppercase",
}));

interface GroupsContainerProps {
  scrollable: boolean;
  children: React.ReactNode;
}

const GroupsContainer = ({ scrollable, children }: GroupsContainerProps) =>
  scrollable ? (
    <ScrollableWithGradients
      maxHeight={SIDEBAR_MAX_HEIGHT}
      pr={2}
      mb={6}
      recalculateDeps={[children]}
    >
      {children}
    </ScrollableWithGradients>
  ) : (
    <Box mb={6}>{children}</Box>
  );

interface ArticleGroupProps {
  label: string;
  items: TimelineArticle[];
}

const ArticleGroup = ({ label, items }: ArticleGroupProps) => {
  if (items.length === 0) return null;

  return (
    <>
      <GroupTitle variant="body3" color="textSecondary">
        {label}
      </GroupTitle>
      {items.map((item) => (
        <TimelineCard key={item.id} item={item} />
      ))}
    </>
  );
};

export const ArivaTimeline = ({ topPadding }: { topPadding?: number }) => {
  const { t } = useTranslation();
  const isSM = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const isLG = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  const { today, future, past, isLoading, isEmpty } = useArivaTimeline();

  return (
    <Wrapper pt={topPadding}>
      <Typography variant={isSM ? "body4" : "body2"} mb={2}>
        {t("dashboardPage.dashboard.arivaTimeline.title")}
      </Typography>
      <GroupsContainer scrollable={!isLG}>
        {isLoading && <LoadingPlaceHolder height="250px" />}
        {isEmpty && (
          <Typography variant="body4" color="textSecondary" py={3}>
            {t("dashboardPage.dashboard.arivaTimeline.empty")}
          </Typography>
        )}
        {!isLoading && (
          <>
            <ArticleGroup
              label={t("dashboardPage.dashboard.arivaTimeline.past")}
              items={past}
            />
            <ArticleGroup
              label={t("dashboardPage.dashboard.arivaTimeline.today")}
              items={today}
            />
            <ArticleGroup
              label={t("dashboardPage.dashboard.arivaTimeline.future")}
              items={future}
            />
          </>
        )}
      </GroupsContainer>
    </Wrapper>
  );
};
